import { useEffect, useCallback } from 'react';
import { UnifiedMessagingService } from '@/services/api/unified-messaging.service';
import type { UnifiedMessage, UnifiedConversation } from '@/types/unified-messaging';

interface UseRealTimeMessagesProps {
  conversationId: string | null;
  conversation: UnifiedConversation | null;
  currentUserId?: string;
  onNewMessage: (message: UnifiedMessage) => void;
  setIsTyping: (typing: boolean) => void;
}

export function useRealTimeMessages({
  conversationId,
  conversation,
  currentUserId,
  onNewMessage,
  setIsTyping
}: UseRealTimeMessagesProps) {
  const isValidConversationId = useCallback((id: string) => {
    return id && id.length > 0 && id !== 'undefined' && id !== 'null';
  }, []);

  useEffect(() => {
    if (!conversationId || !isValidConversationId(conversationId) || !conversation) {
      return;
    }

    // Map conversation type to subscription type
    // Groups use 'direct' subscription, channels use 'channel' subscription
    const subscriptionType = conversation.type === 'channel' ? 'channel' : 'direct';
    const service = UnifiedMessagingService.getInstance();
    
    // Subscribe to messages
    const subscription = service.subscribeToUnifiedMessages(
      conversationId,
      subscriptionType,
      onNewMessage
    );

    // Subscribe to typing indicators
    const typingSubscription = service.subscribeToUnifiedTyping(
      conversationId,
      subscriptionType,
      (typing) => {
        if (typing.user_id !== currentUserId) {
          setIsTyping(true);
          setTimeout(() => setIsTyping(false), 3000);
        }
      }
    );

    return () => {
      service.unsubscribeFromUnifiedMessages(conversationId, subscriptionType);
      service.unsubscribeFromUnifiedTyping(conversationId, subscriptionType);
    };
  }, [conversationId, conversation, onNewMessage, currentUserId, setIsTyping, isValidConversationId]);
}

